import React from 'react';
import { Link } from 'react-router-dom';

const ServicesPage: React.FC = () => {
  const services = [
    {
      title: 'Design Engineering',
      description:
        'Comprehensive engineering design solutions from concept to completion',
      features: [
        'Storage Tank Design',
        'Industrial Shed Engineering',
        'Pressure Vessel Design',
        'Structural Analysis',
        'CAD/CAE Software',
        'Technical Documentation',
      ],
      link: '/services/design-engineering',
      icon: (
        <svg
          className="w-8 h-8"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
          />
        </svg>
      ),
    },
    {
      title: 'Fabrication & Erection',
      description:
        'Expert fabrication and installation services for industrial applications',
      features: [
        'Piping Spool & Manifold',
        'Steel Structural Fabrication',
        'Plant Equipment Fabrication',
        'Sheet Metal Fabrication',
        'Coded Qualified Welding',
        'Custom Solutions',
      ],
      link: '/services/fabrication-erection',
      icon: (
        <svg
          className="w-8 h-8"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z"
          />
        </svg>
      ),
    },
    {
      title: 'Machining',
      description:
        'Precision machining solutions with collaboration and backing of Sohar University',
      features: [
        'Turning & Threading',
        'Milling & Boring',
        'Grinding & Reaming',
        'Flange Machining',
        'Custom Components',
        'Academic Partnership',
      ],
      link: '/services/machining',
      icon: (
        <svg
          className="w-8 h-8"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"
          />
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
          />
        </svg>
      ),
    },
    {
      title: 'Plant Maintenance',
      description:
        'Comprehensive maintenance solutions for industrial facilities',
      features: [
        'Equipment Installation',
        'Machinery Maintenance',
        'Specialized Repairs',
        'Annual Shutdown Support',
        'Emergency Response',
        '24/7 Support',
      ],
      link: '/services/plant-maintenance',
      icon: (
        <svg
          className="w-8 h-8"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"
          />
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
          />
        </svg>
      ),
    },
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <div className="bg-primary-500 text-secondary-500 py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-3xl md:text-5xl font-bold mb-4 animate-fade-in">
              Our Services
            </h1>
            <p
              className="text-lg md:text-2xl text-secondary-100 max-w-3xl mx-auto animate-slide-up"
              style={{ animationDelay: '0.2s' }}
            >
              Comprehensive engineering solutions for industrial applications
            </p>
          </div>
        </div>
      </div>

      {/* Services Overview */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        {/* Introduction */}
        <div
          className="text-center mb-16 animate-fade-in"
          style={{ animationDelay: '0.3s' }}
        >
          <h2 className="text-2xl md:text-3xl font-bold text-primary-500 mb-6">
            Engineering Excellence Across Multiple Disciplines
          </h2>
          <p className="text-base md:text-lg text-gray-700 max-w-4xl mx-auto">
            Harmony Engineering Solution LLC offers in-depth knowledge,
            experience, and technical expertise across multiple industrial
            disciplines. Our commitment to offering creative solutions has
            earned us the confidence of our clients in various projects that are
            small or large, simple or complex.
          </p>
        </div>

        {/* Services Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {services.map((service, index) => (
            <div
              key={index}
              className="bg-white rounded-lg shadow-lg overflow-hidden hover:shadow-xl transition-shadow duration-300 animate-slide-up"
              style={{ animationDelay: `${0.4 + index * 0.1}s` }}
            >
              <div className="p-8">
                {/* Service Header */}
                <div className="flex items-center space-x-4 mb-6">
                  <div className="flex-shrink-0 w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center text-primary-500">
                    {service.icon}
                  </div>
                  <div>
                    <h3 className="text-xl md:text-2xl font-bold text-primary-500">
                      {service.title}
                    </h3>
                  </div>
                </div>

                {/* Service Description */}
                <p className="text-gray-700 mb-6 text-sm md:text-base">
                  {service.description}
                </p>

                {/* Service Features */}
                <div className="mb-6">
                  <h4 className="font-semibold text-gray-800 mb-3">
                    Key Services:
                  </h4>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {service.features.map((feature, featureIndex) => (
                      <div
                        key={featureIndex}
                        className="flex items-center space-x-2"
                      >
                        <div className="w-2 h-2 bg-primary-500 rounded-full flex-shrink-0"></div>
                        <span className="text-sm text-gray-700">{feature}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Learn More Button */}
                <Link
                  to={service.link}
                  className="inline-flex items-center px-6 py-3 bg-primary-500 text-secondary-500 rounded-lg font-semibold hover:bg-primary-600 transition-colors duration-200 cursor-pointer"
                >
                  Learn More
                  <svg
                    className="w-4 h-4 ml-2"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 5l7 7-7 7"
                    />
                  </svg>
                </Link>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Why Choose Us Section */}
      <div className="bg-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div
            className="text-center mb-12 animate-fade-in"
            style={{ animationDelay: '0.8s' }}
          >
            <h2 className="text-2xl md:text-3xl font-bold text-primary-500 mb-4">
              Why Choose Harmony Engineering?
            </h2>
            <p className="text-base md:text-lg text-gray-700 max-w-2xl mx-auto">
              We combine technical expertise with creative problem-solving to
              deliver exceptional results.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                title: 'Technical Expertise',
                description:
                  'Deep knowledge across multiple industrial disciplines with proven track record.',
                icon: (
                  <svg
                    className="w-8 h-8"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"
                    />
                  </svg>
                ),
              },
              {
                title: 'Creative Solutions',
                description:
                  'Innovative approaches to complex engineering challenges, tailored to your needs.',
                icon: (
                  <svg
                    className="w-8 h-8"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M13 10V3L4 14h7v7l9-11h-7z"
                    />
                  </svg>
                ),
              },
              {
                title: 'Project Flexibility',
                description:
                  'Handling projects of all sizes and complexity levels with equal dedication.',
                icon: (
                  <svg
                    className="w-8 h-8"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z"
                    />
                  </svg>
                ),
              },
            ].map((item, index) => (
              <div
                key={index}
                className="text-center animate-slide-up"
                style={{ animationDelay: `${0.9 + index * 0.1}s` }}
              >
                <div className="w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center text-primary-500 mx-auto mb-4">
                  {item.icon}
                </div>
                <h3 className="text-lg md:text-xl font-bold text-primary-500 mb-3">
                  {item.title}
                </h3>
                <p className="text-sm md:text-base text-gray-700">
                  {item.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Industries We Serve */}
      <div className="bg-gray-50 py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div
            className="text-center mb-12 animate-fade-in"
            style={{ animationDelay: '1.2s' }}
          >
            <h2 className="text-2xl md:text-3xl font-bold text-primary-500 mb-4">
              Industries We Serve
            </h2>
            <p className="text-base md:text-lg text-gray-700 max-w-2xl mx-auto">
              Our expertise spans across various industrial sectors in Oman and
              beyond.
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {[
              'Oil & Gas',
              'Petrochemical',
              'Power Generation',
              'Manufacturing',
              'Steel Industry',
              'Mining',
              'Water Treatment',
              'Construction',
            ].map((industry, index) => (
              <div
                key={index}
                className="bg-white p-6 rounded-lg shadow-md text-center hover:shadow-lg transition-shadow duration-200 animate-fade-in"
                style={{ animationDelay: `${1.3 + index * 0.05}s` }}
              >
                <h3 className="font-semibold text-gray-800 text-sm md:text-base">
                  {industry}
                </h3>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Call to Action Section */}
      <div className="bg-primary-500 text-secondary-500 py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2
            className="text-2xl md:text-3xl font-bold mb-4 animate-fade-in"
            style={{ animationDelay: '1.5s' }}
          >
            Ready to Start Your Project?
          </h2>
          <p
            className="text-lg md:text-xl text-secondary-100 mb-8 max-w-2xl mx-auto animate-slide-up"
            style={{ animationDelay: '1.6s' }}
          >
            Contact us today to discuss your engineering needs and discover how
            we can help bring your project to life.
          </p>
          <div
            className="flex flex-col sm:flex-row gap-4 justify-center animate-fade-in"
            style={{ animationDelay: '1.7s' }}
          >
            <Link
              to="/contact"
              className="bg-secondary-500 text-primary-500 px-8 py-3 rounded-lg font-semibold hover:bg-secondary-100 transition-colors cursor-pointer"
            >
              Get Quote
            </Link>
            <Link
              to="/about"
              className="border-2 border-secondary-500 text-secondary-500 px-8 py-3 rounded-lg font-semibold hover:bg-secondary-500 hover:text-primary-500 transition-colors cursor-pointer"
            >
              Learn About Us
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ServicesPage;
